<template>
	<div>

		<div v-if="loader" class="flex center text-center mb-1">
			<i class="fas fa-cog fa-spin fa-2x"></i>
			Loading....
		</div>

		<form v-if="!loader" :action="endPoint" class="list-filter flex box" method="get" ref="filterForm">
			<div class="two form-group">
				<label for="limit"><i class="fas fa-fw fa-list-ul"></i> Show</label>
				<select id="limit" name="limit" @change="onFormChange" v-model="selectedLimit">
					<option value="null">25</option>
					<option value="50">50</option>
					<option value="100">100</option>
				</select>
			</div>

			<div class="two form-group">
				<label for="status"><i class="fas fa-fw fa-archive"></i>Status</label>
				<select id="status" @change="onFormChange" name="status" v-model="selectedStatus">
					<option value="null">All</option>
					<option v-for="state in states" :value="state.value">{{ state.name }}</option>
				</select>
			</div>

			<div class="two form-group">
				<label for="author"><i class="fas fa-fw fa-archive"></i>Written by</label>
				<select id="author" @change="onFormChange" name="author" v-model="selectedAuthor">
					<option value="null">All</option>
					<option v-for="author in authors" :value="author.user_id">{{ author.first_name }} {{ author.last_name }}</option>
				</select>
			</div>

			<div class="six form-group">
				<label for="filter"><i class="fas fa-fw fa-filter"></i> Filter</label>
				<div class="input-group">
					<input @change="checkReset" @keyup="onFilterChange" class="input-small" id="filter" name="filter" placeholder="Filter in Author, Email or Comment" type="text" v-model="urlParams.filter">
					<button :disabled="!this.filterEnabled" @click="clearForm" class="btn btn-primary">Filter</button>
				</div>
			</div>
		</form>

		<div class="mt-4" v-show="thread">
			<h4>Showing Comment Thread</h4>
			<p>
				<a @click.prevent="getThreadById(0)" class="btn btn-md btn-primary" href="#"><i class="fas fa-arrow-left icon-left"></i> Back to All Comments</a>
			</p>
		</div>
		<div class="table-responsive">
			<table class="comments">
				<thead>
					<tr>
						<th>Date</th>
						<th>Article Details</th>
						<th>Comment Details</th>
						<th class="actions">Actions</th>
					</tr>
				</thead>
				<tbody>
					<tr :class="{ 'notApproved': comment.isApproved, 'previous-comment': (comment.parentId !== 0 && !thread) }" :id="comment.id" v-for="(comment, index) in comments">
						<td class="date">{{ comment.datetime }}</td>
						<td>
							<ul class="article-details">
								<li class="article-details__item">
									<strong class="article-details__label">Written by:</strong>
									<span class="article-details__value">{{ comment.articleAuthor }}</span>
								</li>
								<li class="article-details__item">
									<strong class="article-details__label">URL:</strong>
									<span class="article-details__value"><a :href="'https://proprivacy.com' + comment.url" target="_blank" rel="noopener noreferrer">{{ comment.url }}</a></span>
								</li>
							</ul>
						</td>
						<td class="comment">
							<div class="author">
								<span class="author__part"><i class="far fa-user icon-left"></i> <strong>{{ comment.authorName }}</strong></span>
								<span class="author__part"><i class="far fa-envelope icon-left"></i> {{ comment.authorEmail }}</span>
								<span class="author__part"><i class="far fa-id-badge icon-left"></i> <em>{{ comment.authorIp }}</em></span>
							</div>
							<div class="comment-text">{{ comment.body }}</div>
							<strong class="view-previous" v-show="(comment.parentId > 0 && !thread)">This is a reply.
								<a @click="getThreadById(comment.id)" v-if="thread">Hide thread</a>
								<a @click="getThreadById(comment.id)" v-else>Show thread</a>
							</strong>
						</td>
						<td class="nowrap">
							<a @click="approve(comment.id)" class="btn btn-icon btn-success" title="Accept and Publish" v-show="!comment.isApproved"><i class="fas fa-fw fa-check"></i></a>
							<a @click="showReplyModal(index)" class="btn btn-icon btn-primary" title="Reply to Comment"><i class="fas fa-fw fa-reply-all"></i></a>
							<a @click="showEditModal(index)" class="btn btn-icon btn-primary" href="#" title="Comment Edit" v-show="userRoles.includes('comments.update')"><i class="fas fa-fw fa-pencil-alt"></i></a>
							<a @click="viewComment(comment.id)" class="btn btn-icon btn-primary" title="View on Site"><i class="fas fa-fw fa-eye"></i></a>
							<a @click="deleteComment(comment.id)" class="btn btn-icon btn-danger" title="Delete Comment" v-show="userRoles.includes('comments.delete')"><i class="fas fa-fw fa-trash"></i></a>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<modal @close="closeEditModal" v-show="isEditModalVisible">
			<template slot="header">
				<h4 class="modal-title">Edit Comment of {{ currentComment.authorName }}</h4>
			</template>
			<template slot="body">
				<div class="form-group">
					<label for="edit-comment-body">Comment</label>
					<textarea id="edit-comment-body" v-model="currentComment.body"></textarea>
					<p class="input-error" v-if="errors.errors.body">{{ errors.errors.body }}</p>
				</div>
			</template>
			<template slot="footer">
				<a @click="closeEditModal" aria-label="Close modal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Cancel</a>
				<a @click="editComment" class="btn btn-md btn-success"><i class="fas fa-save"></i> Save Changes</a>
			</template>
		</modal>

		<modal @close="closeReplyModal" v-show="isReplyModalVisible">
			<template slot="header">
				<h4 class="modal-title">Reply to Comment of {{ currentComment.authorName }}</h4>
			</template>
			<template slot="body">
				<div class="form-group">
					<label>Comment</label>
					<p>{{ currentBody }}</p>
				</div>
				<div class="form-group">
					<label for="comment-reply-body">Reply</label>
					<textarea id="comment-reply-body" v-model="currentComment.body"></textarea>
					<p class="input-error" v-if="errors.errors.body">{{ errors.errors.body }}</p>
				</div>
			</template>
			<template slot="footer">
				<a @click="closeReplyModal" aria-label="Close modal" class="btn btn-md btn-primary modal-close"><i class="fas fa-times"></i> Cancel</a>
				<a @click="reply" class="btn btn-md btn-success"><i class="fas fa-save"></i> Reply</a>
			</template>
		</modal>
	</div>
</template>

<script>
	import Errors from '../../Utils/Errors.js';
	import modal from '../Modal/DefaultModal.vue';

	import JQuery from 'jquery';

	let $ = JQuery;

	export default {
		components: {
			modal
		},
		props: {
			dataComments: {
				type: Object,
			},
			dataEndpoint: {
				type: String
			},
			dataUrlParams: {
				type: Object,
				required: false
			},
			dataUserRoles: {
				type: Array
			},
			dataAuthors: {
				type: Array
			}
		},
		data() {
			return {
				comments: this.dataComments,
				endPoint: this.dataEndpoint,
				urlParams: this.dataUrlParams || [],
				userRoles: this.dataUserRoles,
				authors: this.dataAuthors,

				isEditModalVisible: false,
				isReplyModalVisible: false,

				currentComment: {
					id: null,
					authorName: null,
					commentable_id: null,
					commentable_type: null,
					body: null
				},
				currentBody: null,
				thread: false,
				selectedLimit: this.dataUrlParams.limit ? this.dataUrlParams.limit : null,
				states: [
					{name: 'Approved', value: true},
					{name: 'Pending', value: false},
				],
				selectedStatus: this.dataUrlParams.status ? this.dataUrlParams.status : null,
				filterEnabled: this.dataUrlParams.filter && this.dataUrlParams.filter.length > 0,
				selectedAuthor: this.dataUrlParams.author ? this.dataUrlParams.author : null,

				errors: new Errors()
			}
		},
		methods: {
			showEditModal: function (id) {
				this.currentComment = this.comments[id];
				this.isEditModalVisible = true;
			},
			closeEditModal: function () {
				this.isEditModalVisible = false;
			},
			showReplyModal: function (id) {
				this.currentComment = JSON.parse(JSON.stringify(this.comments[id])); //remove reactivity a=b b=c c!=a
				this.currentBody = this.currentComment.body;
				this.currentComment.body = null;
				this.isReplyModalVisible = true;
			},
			closeReplyModal: function () {
				this.isReplyModalVisible = false;
			},
			editComment: function () {
				let vm = this;

				axios.put('/api/' + vm.$selectedSite.slug + '/comments/' + vm.currentComment.id, vm.currentComment)
						.then(function () {
							vm.$toastr.success('Comment edited successfully.');
							window.location.reload();
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Edit comment failed.', 'Error');
						});

			},
			reply: function () {
				let vm = this;
				this.approve(vm.currentComment.id);
				axios.post('/api/' + vm.$selectedSite.slug + '/comments/reply', vm.currentComment)
						.then(function () {
							vm.$toastr.success('Comment replied successfully.');
							window.location.reload();
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Reply comment failed.', 'Error');
						});
			},
			deleteComment: function (id) {
				let vm = this;

				axios.delete('/api/' + vm.$selectedSite.slug + '/comments/' + id)
						.then(function () {
							vm.$toastr.success('Comment deleted successfully.');
							window.location.reload();
						})
						.catch(function () {
							vm.$toastr.error('Delete comment failed.', 'Error');
						});
			},
			approve: function (id) {
				let vm = this;

				axios.get('/api/' + vm.$selectedSite.slug + '/comments/' + id + '/approve')
						.then(function () {
							vm.$toastr.success('Comment approved successfully.');
							window.location.reload();
						})
						.catch(function () {
							vm.$toastr.error('Approve comment failed.', 'Error');
						});
			},
			viewComment: function (id) {
				let vm = this;

				axios.get('/api/' + vm.$selectedSite.slug + '/comments/getUrl/' + id)
						.then(function (response) {
							window.open(response.data.url + '#comments', '_blank').focus();
						})
						.catch(function () {
							vm.$toastr.error('Get comment site URL failed.', 'Error');
						});
			},
			getThreadById: function (id) {
				let vm = this;

				if (this.thread === false) {
					this.thread = true;

					$('ul.pagination').hide();

					axios.get('/api/' + vm.$selectedSite.slug + '/comments/' + id + '/thread')
							.then(function (response) {
								vm.comments = response.data.comments;
							})
							.catch(function (e) {
								vm.$toastr.error('Get thread failed.', 'Error');
							});
				} else {
					$('ul.pagination').show();

					this.thread = false;
					window.location.reload();
				}
			},
			clearForm: function () {
				$('input, select').each(function (_, inp) {
					if ($(inp).val() === '' || $(inp).val() === 'null' || $(inp).val() === null)
						inp.disabled = true;
				});
			},
			checkReset: function () {
				if (this.urlParams.filter.length === 0) {
					this.onFormChange();
				}
			},
			onFormChange: function () {
				this.loader = true;
				this.clearForm();
				this.$refs.filterForm.submit();
			},
			onFilterChange: function () {
				this.filterEnabled = this.urlParams.filter && this.urlParams.filter.length > 0;
			},
		}
	}
</script>

<style scoped>

</style>
